/**
 * <delivery-availability>
 * Keeps the "Order today to get it by ..." delivery promise in sync with the
 * selected variant's stock state, so a backordered / out-of-stock size never
 * shows a delivery date next to the "Backordered" notice.
 *
 * Robustness: the initial visibility is also decided in Liquid
 * (main-product.liquid, `when 'delivery'`), but this element does NOT rely on
 * that alone. On connect it reads the CURRENTLY selected variant straight from
 * the product form's `input[name="id"]` and re-applies the correct state — so
 * even if it upgrades late, the page was served from cache, or a variant change
 * fired before its listener attached, it self-corrects instead of leaving a
 * stale delivery date on a backordered size. It then listens for further
 * changes (both the theme's `on:variant:change` event and, as a fallback, the
 * native `change` bubbling from the size radios).
 *
 * Mirrors the stock logic in assets/product-inventory.js:
 *   backordered  -> qty <= 0 && policy === 'continue'
 *   out of stock -> qty <= 0 && !available
 * The delivery message is shown only when the variant is genuinely in stock.
 */
if (!customElements.get('delivery-availability')) {
  class DeliveryAvailability extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.message = this.querySelector('.delivery-message');

      const dataEl = this.querySelector('.js-delivery-inventory');
      try {
        this.inventory = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.inventory = [];
      }

      this.productInfo = this.closest('.product-info');

      // Primary: the theme's variant-change event (carries the variant object).
      const variantPicker = this.productInfo && this.productInfo.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', (evt) => {
          const variant = evt.detail && evt.detail.variant;
          this.sync(variant ? variant.id : null);
        });
      }

      // Fallback: native change bubbling from the size radios. Re-read the
      // form's id input on the next tick (after variant-picker updates it),
      // covering any case where the custom event is missed.
      if (this.productInfo) {
        this.productInfo.addEventListener('change', () => {
          window.setTimeout(() => this.sync(), 0);
        });
      }

      // Self-correct to whatever is selected right now (closes the load race).
      this.sync();
    }

    /**
     * Reads the current variant id (from the arg, else the product form's
     * hidden id input) and shows/hides the delivery message accordingly.
     * @param {?(number|string)} variantId
     */
    sync(variantId) {
      let id = variantId;
      if (!id) {
        const idInput = this.productInfo && this.productInfo.querySelector('input[name="id"]');
        id = idInput ? idInput.value : null;
      }
      if (!id) {
        // No variant resolvable -> hide to be safe (matches the no-variant case).
        this.toggle(false);
        return;
      }
      const data = this.inventory.filter((item) => String(item.id) === String(id))[0];
      this.toggle(this.shouldShow(data));
    }

    /**
     * Whether the delivery promise should be visible for this variant.
     * @param {?object} data - variant inventory record
     * @returns {boolean}
     */
    shouldShow(data) {
      if (!data) return true;
      const tracked = !!data.inventory_management && data.inventory_management.length > 0;
      if (tracked && data.inventory_quantity <= 0) {
        if (data.inventory_policy === 'continue' || data.available === false) {
          return false;
        }
      }
      return true;
    }

    /**
     * @param {boolean} show
     */
    toggle(show) {
      if (this.message) this.message.hidden = !show;
    }
  }

  customElements.define('delivery-availability', DeliveryAvailability);
}
